import { CalendarState, SelectablePeriodTemplateState } from 'adapter/in/common/components/PeriodSelector';
import i18n from 'asset/translation';
import { endOfSemester, startOfSemester, subSemesters } from 'adapter/in/common/utils/DateUtils';
import {
    subDays,
    subMonths,
    subQuarters,
    subYears,
    subWeeks,
    endOfMonth,
    startOfMonth,
    endOfYear,
    startOfYear,
    startOfQuarter,
    startOfWeek,
    endOfQuarter,
    endOfWeek,
    format,
    differenceInWeeks,
    isToday,
    isLastDayOfMonth,
    getMonth,
    getYear,
    getDate,
    differenceInMonths,
    differenceInYears,
    isFirstDayOfMonth,
    differenceInDays,
    isMonday,
    isSunday,
    isSameWeek,
    isSameDay,
    isSameMonth,
    isYesterday,
    endOfDay,
} from 'date-fns';
import { PeriodFormState, PeriodSelectorPeriods } from 'adapter/in/common/form/types';

export const formatRollingLabel = (formState: PeriodFormState, periodValues: SelectablePeriodTemplateState[] | undefined): string => {
    const foundLabel = periodValues?.find((periodTemplate: SelectablePeriodTemplateState) =>
        areRollingStatesEqual(periodTemplate.state, formState)
    )?.label;
    return foundLabel || i18n.t('common.periodSelector.customLabel');
};

export const formatFixedDateLabel = (calendar: CalendarState & { isToEnd: boolean }, dateFormat: string | undefined) =>
    i18n.t('common.periodSelector.fixedPeriodLabel', {
        from: format(calendar.startDate, dateFormat || 'yyyy-MM-dd'),
        to: format(calendar.endDate, dateFormat || 'yyyy-MM-dd'),
        fixedEnd: calendar.isToEnd ? i18n.t('common.periodSelector.toEnd') : i18n.t('common.periodSelector.toNow'),
    });

export const areRollingStatesEqual = (incomingState: PeriodFormState, currentState: PeriodFormState) =>
    incomingState.isWithCurrent === currentState.isWithCurrent &&
    incomingState.isToEnd === currentState.isToEnd &&
    incomingState.number === currentState.number &&
    incomingState.period === currentState.period;

export const areFixedStatesEqual = (
    incomingState: CalendarState & { isToEnd: boolean },
    currentState: CalendarState & { isToEnd: boolean }
) =>
    incomingState.isToEnd === currentState.isToEnd &&
    isSameDay(incomingState.endDate, currentState.endDate) &&
    isSameDay(incomingState.startDate, currentState.startDate);

export const parseCalendarStateToFormState = (calendar: CalendarState, now: Date): PeriodFormState => {
    const startDate = calendar.startDate;
    const endDate = calendar.endDate;

    const isFirstDayOfYear = (dateToCheck: Date) => {
        const currentYear = getYear(now);

        return getMonth(dateToCheck) === 0 && getDate(dateToCheck) === 1 && getYear(dateToCheck) <= currentYear;
    };
    const isEndOfPreviousYear = (dateToCheck: Date) => {
        const currentYear = getYear(now);
        return getMonth(dateToCheck) === 11 && getDate(dateToCheck) === 31 && getYear(dateToCheck) === currentYear - 1;
    };
    const isEndOfCurrentYear = (dateToCheck: Date) => {
        const currentYear = getYear(now);
        return getMonth(dateToCheck) === 11 && getDate(dateToCheck) === 31 && getYear(dateToCheck) === currentYear;
    };

    const isEndOfPreviousMonth = (dateToCheck: Date) => {
        const lastDayOfCurrentMonth = endOfMonth(now);
        const lastDayOfPreviousMonth = endOfMonth(subMonths(lastDayOfCurrentMonth, 1));
        return isSameDay(dateToCheck, lastDayOfPreviousMonth);
    };

    const isEndOfCurrentMonth = (dateToCheck: Date) => isSameMonth(dateToCheck, now) && isLastDayOfMonth(dateToCheck);

    const isEndOfCurrentWeek = (dateToCheck: Date) => isSameWeek(dateToCheck, now, { weekStartsOn: 1 }) && isSunday(dateToCheck);

    const isEndOfPreviousWeek = (dateToCheck: Date) => {
        const startOfCurrentWeek = startOfWeek(now, { weekStartsOn: 1 });
        const startOfPreviousWeek = subWeeks(startOfCurrentWeek, 1);

        return isSameWeek(dateToCheck, startOfPreviousWeek, { weekStartsOn: 1 }) && isSunday(dateToCheck);
    };

    if ((isFirstDayOfYear(startDate) && isEndOfPreviousYear(endDate)) || (isFirstDayOfYear(startDate) && isEndOfCurrentYear(endDate))) {
        return {
            period: PeriodSelectorPeriods.YEAR,
            number: differenceInYears(now, startDate) + (isToday(endDate) || isEndOfCurrentYear(endDate) ? 1 : 0),
            isWithCurrent: isEndOfCurrentYear(endDate),
            isToEnd: isEndOfCurrentYear(endDate) || isEndOfPreviousYear(endDate),
        };
    }

    if ((isFirstDayOfMonth(startDate) && isEndOfPreviousMonth(endDate)) || (isFirstDayOfMonth(startDate) && isEndOfCurrentMonth(endDate))) {
        return {
            period: PeriodSelectorPeriods.MONTH,
            number: differenceInMonths(now, startDate) + (isToday(endDate) || isEndOfCurrentMonth(endDate) ? 1 : 0),
            isWithCurrent: isEndOfCurrentMonth(endDate),
            isToEnd: isEndOfCurrentMonth(endDate) || isEndOfPreviousMonth(endDate),
        };
    }

    if ((isMonday(startDate) && isEndOfPreviousWeek(endDate)) || (isMonday(startDate) && isEndOfCurrentWeek(endDate))) {
        return {
            period: PeriodSelectorPeriods.WEEK,
            number: differenceInWeeks(now, startDate) + (isToday(endDate) || isEndOfCurrentWeek(endDate) ? 1 : 0),
            isWithCurrent: isEndOfCurrentWeek(endDate),
            isToEnd: isEndOfCurrentWeek(endDate) || isEndOfPreviousWeek(endDate),
        };
    }

    if ((isToday(startDate) && isToday(endDate)) || isYesterday(endDate) || isToday(endDate)) {
        return {
            period: PeriodSelectorPeriods.DAY,
            number: differenceInDays(now, startDate) + (isToday(endDate) ? 1 : 0),
            isWithCurrent: isToday(endDate),
            isToEnd: true,
        };
    }

    return {
        period: PeriodSelectorPeriods.DAY,
        number: 0,
        isWithCurrent: true,
        isToEnd: true,
    };
};

export const parseFormStateToCalendarState = (
    number: number,
    period: PeriodSelectorPeriods,
    isWithCurrent: boolean,
    now: Date
): CalendarState => {
    switch (period) {
        case PeriodSelectorPeriods.YEAR:
            return {
                startDate: startOfYear(subYears(now, isWithCurrent ? number - 1 : number)),
                endDate: endOfYear(subYears(now, isWithCurrent ? 0 : 1)),
            };
        case PeriodSelectorPeriods.MONTH:
            return {
                startDate: startOfMonth(subMonths(now, isWithCurrent ? number - 1 : number)),
                endDate: endOfMonth(subMonths(now, isWithCurrent ? 0 : 1)),
            };
        case PeriodSelectorPeriods.SEMESTER:
            return {
                startDate: startOfSemester(subSemesters(now, isWithCurrent ? number - 1 : number)),
                endDate: endOfSemester(subSemesters(now, isWithCurrent ? 0 : 1)),
            };
        case PeriodSelectorPeriods.QUARTER:
            return {
                startDate: startOfQuarter(subQuarters(now, isWithCurrent ? number - 1 : number)),
                endDate: endOfQuarter(subQuarters(now, isWithCurrent ? 0 : 1)),
            };
        case PeriodSelectorPeriods.WEEK:
            return {
                startDate: startOfWeek(subWeeks(now, isWithCurrent ? number - 1 : number), { weekStartsOn: 1 }),
                endDate: endOfWeek(subWeeks(now, isWithCurrent ? 0 : 1), { weekStartsOn: 1 }),
            };
        case PeriodSelectorPeriods.DAY:
        default:
            return {
                startDate: subDays(now, isWithCurrent ? number - 1 : number),
                endDate: endOfDay(subDays(now, isWithCurrent ? 0 : 1)),
            };
    }
};

export const parsePeriodCodeToPeriodTemplateState = (code: string): PeriodFormState => {
    const splitCode = code.split('_');
    return {
        isWithCurrent: splitCode[0] === 'CURRENT',
        number: parseFloat(splitCode[1]),
        period: splitCode[2] as PeriodSelectorPeriods,
        isToEnd: splitCode[3] === 'TO-END',
    };
};
